import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { partialAPI, mockAPI } from '@/api/client';
import { toast } from 'sonner';
import { formatDate, formatTimeRange } from '@/utils/formatters';

const initialFormData = {
  module: 'LISTENING',
  exam_date: '',
  start_time: '',
  end_time: '',
  total_slots: 20,
  room: '',
  campus: '',
  notes: '',
  category: 'PB',
  lrw_date: '',
  lrw_start: '',
  lrw_end: '',
  lrw_total_seats: 25,
  external_only: false,
  speaking_dates: [''],
  mock_speaking_slots: [],
  speaking_slots: []
};

const CreateExamModal = ({ open, onOpenChange, onExamCreated }) => {
  const [examType, setExamType] = useState('partial');
  const [examFormData, setExamFormData] = useState(initialFormData);
  const [creatingExam, setCreatingExam] = useState(false);

  const handleCreateExam = async () => {
    setCreatingExam(true);
    try {
      if (examType === 'partial') {
        // Speaking module: Send generated slots
        if (examFormData.module === 'SPEAKING') {
          if (examFormData.speaking_slots.length === 0) {
            toast.error('Please generate at least one speaking slot');
            setCreatingExam(false);
            return;
          }
          
          await partialAPI.create({
            module: 'SPEAKING',
            speaking_slots: examFormData.speaking_slots,
            campus: examFormData.campus || null,
            notes: examFormData.notes || null
          });
          toast.success(`${examFormData.speaking_slots.length} speaking slots created successfully!`);
        } 
        // Regular modules
        else {
          await partialAPI.create({
            module: examFormData.module,
            exam_date: examFormData.exam_date,
            start_time: examFormData.start_time,
            end_time: examFormData.end_time,
            total_slots: parseInt(examFormData.total_slots),
            room: examFormData.room || null,
            campus: examFormData.campus || null,
            notes: examFormData.notes || null,
            category: examFormData.category
          });
          toast.success('Partial exam created successfully!');
        }
      } else {
        // Mock exam
        if (examFormData.mock_speaking_slots.length === 0) {
          toast.error('Please generate at least one speaking slot');
          return;
        }
        await mockAPI.create({
          lrw_date: examFormData.lrw_date,
          lrw_start: examFormData.lrw_start,
          lrw_end: examFormData.lrw_end,
          lrw_total_seats: parseInt(examFormData.lrw_total_seats),
          campus: examFormData.campus || null,
          notes: examFormData.notes || null,
          category: examFormData.category,
          external_only: !!examFormData.external_only,
          speaking_slots: examFormData.mock_speaking_slots
        });
        toast.success(`Mock exam created with ${examFormData.mock_speaking_slots.length} speaking slots!`);
      }
      
      onOpenChange(false);
      setExamFormData(initialFormData);
      if (onExamCreated) onExamCreated();
    } catch (error) {
      console.error('Failed to create exam:', error);
      toast.error(error.response?.data?.detail || 'Failed to create exam');
    } finally {
      setCreatingExam(false);
    }
  };

  const generateSpeakingSlots = (prefix = '') => {
    const date = document.getElementById(`${prefix}gen_date`)?.value || document.getElementById(`${prefix}speak_gen_date`)?.value;
    const start = document.getElementById(`${prefix}gen_start`)?.value || document.getElementById(`${prefix}speak_gen_start`)?.value;
    const end = document.getElementById(`${prefix}gen_end`)?.value || document.getElementById(`${prefix}speak_gen_end`)?.value;
    const interval = parseInt(document.getElementById(`${prefix}gen_interval`)?.value || document.getElementById(`${prefix}speak_gen_interval`)?.value);
    const room = document.getElementById(`${prefix}gen_room`)?.value || document.getElementById(`${prefix}speak_gen_room`)?.value;
    
    if (!date || !start || !end || !interval) {
      toast.error('Please fill all required fields');
      return [];
    }
    
    const slots = [];
    const [startHr, startMin] = start.split(':').map(Number);
    const [endHr, endMin] = end.split(':').map(Number);
    let currentMin = startHr * 60 + startMin;
    const endMinutes = endHr * 60 + endMin;
    
    while (currentMin + interval <= endMinutes) {
      const slotStart = `${String(Math.floor(currentMin / 60)).padStart(2, '0')}:${String(currentMin % 60).padStart(2, '0')}`;
      currentMin += interval;
      const slotEnd = `${String(Math.floor(currentMin / 60)).padStart(2, '0')}:${String(currentMin % 60).padStart(2, '0')}`;
      slots.push({ date, start: slotStart, end: slotEnd, room });
    }
    
    return slots;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Exam Schedule</DialogTitle>
          <DialogDescription>
            Create new Partial or Mock exam slots
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Exam Type Selector */}
          <div className="space-y-2">
            <Label>Exam Type</Label>
            <Select value={examType} onValueChange={setExamType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="partial">Partial Exam (L/R/W/S)</SelectItem>
                <SelectItem value="mock">Mock Test (LRW + Speaking)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {examType === 'partial' ? (
            // Partial Exam Form
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="module">Module *</Label>
                  <Select 
                    value={examFormData.module} 
                    onValueChange={(val) => setExamFormData({...examFormData, module: val})}
                  >
                    <SelectTrigger id="module">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LISTENING">Listening</SelectItem>
                      <SelectItem value="READING">Reading</SelectItem>
                      <SelectItem value="WRITING">Writing</SelectItem>
                      <SelectItem value="SPEAKING">Speaking</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={examFormData.module === 'SPEAKING' ? 'SHARED' : examFormData.category} 
                    onValueChange={(val) => setExamFormData({...examFormData, category: val})}
                    disabled={examFormData.module === 'SPEAKING'}
                  >
                    <SelectTrigger id="category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PB">Paper Based (PB)</SelectItem>
                      <SelectItem value="CD">Computer Delivered (CD)</SelectItem>
                      {examFormData.module === 'SPEAKING' && <SelectItem value="SHARED">Shared (All)</SelectItem>}
                    </SelectContent>
                  </Select>
                  {examFormData.module === 'SPEAKING' && (
                    <p className="text-xs text-gray-500">Speaking is shared between PB & CD</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="exam_date">Exam Date *</Label>
                  <Input 
                    id="exam_date"
                    type="date" 
                    value={examFormData.exam_date}
                    onChange={(e) => setExamFormData({...examFormData, exam_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              {/* Hide these fields for Speaking module - use Generator instead */}
              {examFormData.module !== 'SPEAKING' && (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_time">Start Time *</Label>
                      <Input 
                        id="start_time"
                        type="time" 
                        value={examFormData.start_time}
                        onChange={(e) => setExamFormData({...examFormData, start_time: e.target.value})}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="end_time">End Time *</Label>
                      <Input 
                        id="end_time"
                        type="time" 
                        value={examFormData.end_time}
                        onChange={(e) => setExamFormData({...examFormData, end_time: e.target.value})}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="total_slots">Total Slots *</Label>
                      <Input 
                        id="total_slots"
                        type="number" 
                        min="1"
                        value={examFormData.total_slots}
                        onChange={(e) => setExamFormData({...examFormData, total_slots: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="room">Room</Label>
                      <Input 
                        id="room"
                        value={examFormData.room}
                        onChange={(e) => setExamFormData({...examFormData, room: e.target.value})}
                        placeholder="e.g., Room 101"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="campus">Campus</Label>
                      <Input 
                        id="campus"
                        value={examFormData.campus}
                        onChange={(e) => setExamFormData({...examFormData, campus: e.target.value})}
                        placeholder="e.g., Radiance Education"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Speaking Module - Only Campus and Generator */}
              {examFormData.module === 'SPEAKING' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="campus_speaking">Campus *</Label>
                    <Input 
                      id="campus_speaking"
                      value={examFormData.campus}
                      onChange={(e) => setExamFormData({...examFormData, campus: e.target.value})}
                      placeholder="e.g., Radiance Education"
                      required
                    />
                  </div>

                  <div className="space-y-3 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                    <div className="font-semibold text-blue-800">Speaking Slot Generator</div>
                    <p className="text-xs text-gray-600">Generate individual time slots with room assignments</p>
                  
                    <div className="grid grid-cols-5 gap-2">
                      <Input type="date" placeholder="Date" id="speak_gen_date" />
                      <Input type="time" placeholder="Start" id="speak_gen_start" defaultValue="10:00" />
                      <Input type="time" placeholder="End" id="speak_gen_end" defaultValue="18:00" />
                      <Input type="number" placeholder="Interval (mins)" id="speak_gen_interval" defaultValue="20" min="5" />
                      <Input placeholder="Room (optional)" id="speak_gen_room" />
                    </div>
                    
                    <Button 
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        const slots = generateSpeakingSlots('speak_');
                        if (slots.length > 0) {
                          setExamFormData({
                            ...examFormData,
                            speaking_slots: [...examFormData.speaking_slots, ...slots]
                          });
                          toast.success(`${slots.length} slots generated!`);
                          document.getElementById('speak_gen_date').value = '';
                          document.getElementById('speak_gen_room').value = '';
                        }
                      }}
                    >
                      + Generate Slots
                    </Button>
                    
                    {/* Display generated slots */}
                    {examFormData.speaking_slots.length > 0 && (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        <div className="text-sm font-semibold">Generated Slots ({examFormData.speaking_slots.length}):</div>
                        <div className="grid grid-cols-2 gap-2">
                          {examFormData.speaking_slots.map((slot, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                              <div className="text-xs">
                                <div className="font-semibold">{formatDate(slot.date)}</div>
                                <div>{formatTimeRange(slot.start, slot.end)}</div>
                                {slot.room && <div className="text-gray-500">Room: {slot.room}</div>}
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newSlots = examFormData.speaking_slots.filter((_, i) => i !== idx);
                                  setExamFormData({...examFormData, speaking_slots: newSlots});
                                }}
                              >
                                🗑️
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input 
                  id="notes"
                  value={examFormData.notes}
                  onChange={(e) => setExamFormData({...examFormData, notes: e.target.value})}
                  placeholder="Any additional information"
                />
              </div>
            </>
          ) : (
            // Mock Exam Form
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lrw_date">LRW Date *</Label>
                  <Input 
                    id="lrw_date"
                    type="date" 
                    value={examFormData.lrw_date}
                    onChange={(e) => setExamFormData({...examFormData, lrw_date: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category_mock">Category *</Label>
                  <Select 
                    value={examFormData.category} 
                    onValueChange={(val) => setExamFormData({...examFormData, category: val})}
                  >
                    <SelectTrigger id="category_mock">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PB">Paper Based (PB)</SelectItem>
                      <SelectItem value="CD">Computer Delivered (CD)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lrw_total_seats">LRW Total Seats *</Label>
                  <Input 
                    id="lrw_total_seats"
                    type="number" 
                    min="1"
                    value={examFormData.lrw_total_seats}
                    onChange={(e) => setExamFormData({...examFormData, lrw_total_seats: e.target.value})}
                    required
                  />
                </div>
              </div>

              {/* External Only Toggle */}
              <div
                className={`flex items-start gap-3 p-3 rounded-lg border transition ${examFormData.external_only ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`}
              >
                <input
                  id="external_only"
                  data-testid="external-only-checkbox"
                  type="checkbox"
                  checked={!!examFormData.external_only}
                  onChange={(e) => setExamFormData({ ...examFormData, external_only: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                />
                <div className="flex-1">
                  <Label htmlFor="external_only" className="font-medium cursor-pointer flex items-center gap-2">
                    For External Students Only
                    {examFormData.external_only && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-200 text-amber-900 font-semibold">EXTERNAL</span>
                    )}
                  </Label>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    If checked, this mock will <strong>not</strong> appear in any internal student's profile. It will only be visible on the public <code className="px-1 bg-white rounded">/mock-booking</code> page for external candidates, and admins can manually book it from the admin panel.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lrw_start">LRW Start Time *</Label>
                  <Input 
                    id="lrw_start"
                    type="time" 
                    value={examFormData.lrw_start}
                    onChange={(e) => setExamFormData({...examFormData, lrw_start: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lrw_end">LRW End Time *</Label>
                  <Input 
                    id="lrw_end"
                    type="time" 
                    value={examFormData.lrw_end}
                    onChange={(e) => setExamFormData({...examFormData, lrw_end: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="campus_mock">Campus</Label>
                <Input 
                  id="campus_mock"
                  value={examFormData.campus}
                  onChange={(e) => setExamFormData({...examFormData, campus: e.target.value})}
                  placeholder="e.g., Radiance Education"
                />
              </div>

              <div className="space-y-2">
                <Label>Speaking Slot Generator</Label>
                <div className="grid grid-cols-5 gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-xs">Date *</Label>
                    <Input type="date" placeholder="Date" id="gen_date" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Start Time *</Label>
                    <Input type="time" placeholder="Start" id="gen_start" defaultValue="10:00" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">End Time *</Label>
                    <Input type="time" placeholder="End" id="gen_end" defaultValue="18:00" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Interval (mins) *</Label>
                    <Input type="number" placeholder="20" id="gen_interval" defaultValue="20" min="5" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Room</Label>
                    <Input type="text" placeholder="e.g., Room 1" id="gen_room" />
                  </div>
                </div>
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const slots = generateSpeakingSlots('');
                    if (slots.length > 0) {
                      setExamFormData({
                        ...examFormData,
                        mock_speaking_slots: [...examFormData.mock_speaking_slots, ...slots]
                      });
                      toast.success(`${slots.length} speaking slots generated!`);
                      document.getElementById('gen_date').value = '';
                      document.getElementById('gen_room').value = '';
                    }
                  }}
                >
                  + Generate Speaking Slots
                </Button>

                {examFormData.mock_speaking_slots.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    <div className="text-sm font-semibold">Generated Slots ({examFormData.mock_speaking_slots.length}):</div>
                    <div className="grid grid-cols-2 gap-2">
                      {examFormData.mock_speaking_slots.map((slot, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                          <div className="text-xs">
                            <div className="font-semibold">{slot.date}</div>
                            <div>{slot.start} - {slot.end}</div>
                            {slot.room && <div className="text-blue-600">Room: {slot.room}</div>}
                          </div>
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              const newSlots = examFormData.mock_speaking_slots.filter((_, i) => i !== index);
                              setExamFormData({...examFormData, mock_speaking_slots: newSlots});
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes_mock">Notes</Label>
                <Input 
                  id="notes_mock"
                  value={examFormData.notes}
                  onChange={(e) => setExamFormData({...examFormData, notes: e.target.value})}
                  placeholder="Any additional information"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={creatingExam}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateExam}
            disabled={creatingExam}
            className="bg-gradient-to-r from-blue-600 to-purple-600"
          >
            {creatingExam ? 'Creating...' : 'Create Exam'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateExamModal;
